/** @jsx jsx */
import { jsx } from '@emotion/react';
import { useRef, useState } from 'react';
import { Box } from 'boxible';
import styled from '@emotion/styled';
import { colors, media } from '~/pages/research/theme';
import { Section } from '~/pages/research/components/section';
import { TabAccordionCombo } from '~/pages/research/components/tab-accordion-combo';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronUp } from '@fortawesome/free-solid-svg-icons/faChevronUp';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons/faChevronDown';
import Dialog from '~/components/dialog/dialog';
import { isMobileDisplay } from '~/helpers/device';
const MemberGrid = styled.div({
    display: 'grid',
    gap: '2rem',
    padding: '4rem',
    gridTemplateColumns: 'repeat(auto-fit, minmax(10em, 1fr))',
    [media.mobile]: {
        gridTemplateColumns: 'repeat(auto-fit, minmax(8em, 1fr))',
        gap: '3rem',
        padding: '2rem'
    }
});
const CollaboratorGrid = styled.div({
    display: 'grid',
    gap: '2rem',
    padding: '4rem',
    gridTemplateColumns: 'repeat(auto-fit, minmax(10em, 1fr))',
    [media.mobile]: {
        gridTemplateColumns: 'repeat(auto-fit, minmax(10em, 1fr))',
        gap: '3rem',
        padding: '2rem'
    }
});
const AlumniGrid = styled(Box)({
    flexDirection: 'column',
    padding: '4rem',
    display: 'grid',
    gap: '1rem',
    fontSize: '18px',
    [media.mobile]: {
        textAlign: 'center',
        padding: '2rem',
        display: 'grid',
        gap: '3rem',
        fontSize: '16px'
    }
});
export const MembersSection = ({ data: { peopleHeader, currentMembers, collaboratingResearchers, alumni } }) => {
    const [viewAll, setViewAll] = useState(!isMobileDisplay());
    const membersRef = useRef(null);
    const scrollToMembers = () => {
        var _a;
        (_a = membersRef.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView({ behavior: 'smooth' });
    };
    const members = viewAll ? currentMembers : currentMembers.slice(0, 4);
    return (jsx(Section, { backgroundColor: colors.lightGrayBackground },
        jsx("h2", { className: "pb-2", ref: membersRef }, peopleHeader),
        jsx(TabAccordionCombo, null,
            jsx("div", { label: "Current Members", selected: true },
                jsx(MemberGrid, { id: "member-grid" }, members.map((member, index) => (jsx(Member, { member: member, key: index, displayName: member.firstName })))),
                jsx("button", { type: "button", className: "mobile-only view-all-toggle", "aria-expanded": viewAll, "aria-controls": "member-grid", onClick: () => {
                        if (viewAll) {
                            scrollToMembers();
                        }
                        setViewAll(!viewAll);
                    }, css: {
                        color: colors.linkText
                    } },
                    jsx(FontAwesomeIcon, { icon: viewAll ? faChevronUp : faChevronDown }),
                    "\u00A0",
                    viewAll ? 'View Less' : 'View All Current Members')),
            jsx("div", { label: "Collaborating Researchers" },
                jsx(CollaboratorGrid, null, collaboratingResearchers.map((member, index) => (jsx(Member, { member: member, key: index, displayName: `${member.firstName} ${member.lastName}` }))))),
            jsx("div", { label: "Alumni" },
                jsx(AlumniGrid, null, alumni.map((alumnus, index) => (jsx(Alumnus, { alumnus: alumnus, key: index }))))))));
};
const MemberModal = ({ member, isOpen, onHide }) => {
    return (jsx(Dialog, { className: "member-modal", onPutAway: onHide, isOpen: isOpen, closeOnOutsideClick: true },
        jsx(MemberDetails, { member: member })));
};
const Member = ({ member, displayName }) => {
    const [show, setShow] = useState(false);
    return (jsx(Box, { direction: "column", align: "center", className: "text-center" },
        jsx("img", { height: 145, width: 145, alt: member.photo.title, src: member.photo.file }),
        jsx("span", { css: { marginTop: '1.5rem', textAlign: 'center' }, className: "link-text", tabIndex: 0, role: "button", onClick: () => setShow(true) }, displayName),
        jsx("small", { css: { textAlign: 'center' } }, member.title),
        jsx(MemberModal, { member: member, isOpen: show, onHide: () => setShow(false) })));
};
const MemberDetails = ({ member }) => {
    return (jsx(Box, { css: {
            fontSize: 15,
            margin: '20px'
        } },
        jsx(Box, { gap: "large" },
            jsx(MemberImage, { className: "desktop-only", src: member.photo.file, alt: member.firstName }),
            jsx(Box, { direction: "column", gap: "large" },
                jsx(MemberInfo, { member: member }),
                jsx(MemberEducation, { member: member }),
                jsx(MemberResearchInterest, { member: member }),
                jsx(MemberBio, { member: member }),
                jsx(MemberLinks, { member: member })))));
};
const MemberImage = styled.img({
    marginBottom: '.5rem',
    width: 145,
    height: 145,
    [media.mobile]: {
        width: 75,
        height: 75
    }
});
const MemberInfo = ({ member }) => {
    return (jsx(Box, { align: "center", gap: "large" },
        jsx(MemberImage, { className: "mobile-only", src: member.photo.file, alt: member.firstName }),
        jsx(Box, { direction: "column" },
            jsx("h4", null,
                member.firstName,
                " ",
                member.lastName),
            jsx("span", { css: { color: colors.grayText } }, member.longTitle || member.title))));
};
const MemberEducation = ({ member }) => {
    if (!member.education) {
        return null;
    }
    return (jsx(Box, { direction: "column" },
        jsx("h4", null, "Education"),
        jsx("span", null,
            member.education,
            jsx("br", null),
            member.specialization && (jsx("span", { css: { color: colors.grayText } }, member.specialization)))));
};
const MemberResearchInterest = ({ member }) => {
    if (!member.researchInterest) {
        return null;
    }
    return (jsx(Box, { direction: "column" },
        jsx("h4", null, "Research Interest"),
        jsx("span", null, member.researchInterest)));
};
export const MemberBio = ({ member }) => {
    return (jsx(Box, { direction: "column" },
        jsx("h4", null, "Bio"),
        jsx("span", null, member.bio)));
};
const MemberLinks = ({ member }) => {
    return (jsx(Box, { gap: "large" },
        member.linkedIn && (jsx("a", { href: member.linkedIn, target: "_blank", rel: "noreferrer" }, "LinkedIn")),
        member.googleScholar && (jsx("a", { href: member.googleScholar, target: "_blank", rel: "noreferrer" }, "Google Scholar")),
        member.website && (jsx("a", { href: member.website, target: "_blank", rel: "noreferrer" }, "Website"))));
};
const Alumnus = ({ alumnus }) => (jsx(Box, { direction: { mobile: 'column' }, align: "center", justify: "center", gap: true },
    jsx("a", { css: { flex: 1 }, href: alumnus.linkedIn, target: "_blank", rel: "noreferrer" }, alumnus.name),
    jsx("span", { css: { flex: 3, color: colors.grayText } }, alumnus.title)));
